import React, { useState } from "react";
import {
    Box,
    Button,
    ChakraProvider,
    useToast,
    VStack,
    Heading,
    Text,
    Icon,
    Badge,
    Divider,
    Alert,
    AlertIcon,
    AlertDescription,
    Progress,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import NavigationMenu from "../Navigation/NavigationMenu";

const API_BASE = process.env.REACT_APP_BACKEND_API || "https://kidbackend.codingindia.co.in";

const JsonBackup = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [lastDownloaded, setLastDownloaded] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const handleJsonBackup = async () => {
        setIsLoading(true);
        const accessToken = localStorage.getItem("access_token");

        try {
            const response = await fetch(
                `${API_BASE}/HumanResource/api/database/backup/json/`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `Server responded with ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Use content-disposition filename if available, else default
            const disposition = response.headers.get("content-disposition");
            let filename = "kidney_backup.json";
            if (disposition && disposition.includes("filename=")) {
                filename = disposition.split("filename=")[1].replace(/"/g, "").trim();
            }

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            const now = new Date().toLocaleString();
            setLastDownloaded(now);

            toast({
                title: "Backup Downloaded",
                description: `JSON backup saved as "${filename}".`,
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        } catch (error) {
            toast({
                title: "Backup Failed",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <NavigationMenu />
            <ChakraProvider>
                <Box
                    minH="100vh"
                    bg="gray.50"
                    py={10}
                    px={4}
                >
                    <Box maxW="680px" mx="auto">
                        {/* Header */}
                        <VStack spacing={2} mb={8} align="start">
                            <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                                JSON Backup
                            </Badge>
                            <Heading as="h1" size="xl" color="gray.800">
                                Download JSON Backup
                            </Heading>
                            <Text color="gray.500" fontSize="md">
                                Export the entire database as a portable JSON file for cross-platform restoration.
                            </Text>
                        </VStack>

                        <VStack spacing={5} align="stretch">
                            {/* Info Card */}
                            <Box
                                bg="white"
                                border="1px"
                                borderColor="green.100"
                                borderRadius="xl"
                                p={6}
                                shadow="sm"
                            >
                                <Heading as="h3" size="sm" color="green.700" mb={3}>
                                    What this backup includes
                                </Heading>
                                <VStack align="start" spacing={2} color="gray.600" fontSize="sm">
                                    <Text>✅ All employee records and personal data</Text>
                                    <Text>✅ Leave requests and summaries</Text>
                                    <Text>✅ HR Policies, Circulars and Formats</Text>
                                    <Text>✅ Salary, Licence, NABH Policy data</Text>
                                    <Text>✅ Master records and user configurations</Text>
                                </VStack>
                            </Box>

                            {/* Alert */}
                            <Alert status="info" borderRadius="lg" bg="blue.50" border="1px" borderColor="blue.200">
                                <AlertIcon color="blue.500" />
                                <AlertDescription fontSize="sm" color="blue.700">
                                    The JSON format is portable and can be used to restore data on a different database without SQL schema conflicts.
                                </AlertDescription>
                            </Alert>

                            <Divider />

                            {/* Download Button */}
                            <Box
                                bg="white"
                                borderRadius="xl"
                                p={6}
                                shadow="sm"
                                border="1px"
                                borderColor="gray.100"
                            >
                                {isLoading && (
                                    <Box mb={4}>
                                        <Text fontSize="sm" color="gray.500" mb={2}>
                                            Generating JSON backup, please wait...
                                        </Text>
                                        <Progress size="xs" isIndeterminate colorScheme="green" borderRadius="full" />
                                    </Box>
                                )}
                                <Button
                                    colorScheme="green"
                                    size="lg"
                                    w="full"
                                    onClick={handleJsonBackup}
                                    isLoading={isLoading}
                                    loadingText="Generating Backup..."
                                    leftIcon={
                                        <Icon viewBox="0 0 24 24" boxSize={5}>
                                            <path fill="currentColor" d="M12 16l-5-5 1.41-1.41L11 13.17V4h2v9.17l2.59-2.58L17 11l-5 5zm-6 2h12v2H6v-2z" />
                                        </Icon>
                                    }
                                    borderRadius="lg"
                                    fontWeight="semibold"
                                    fontSize="md"
                                    py={6}
                                >
                                    Download JSON Backup
                                </Button>

                                {lastDownloaded && (
                                    <Text fontSize="xs" color="gray.400" mt={3} textAlign="center">
                                        Last downloaded: {lastDownloaded}
                                    </Text>
                                )}
                            </Box>

                            {/* Footer Actions */}
                            <Button
                                variant="ghost"
                                colorScheme="gray"
                                onClick={() => navigate("/json-restore")}
                                size="sm"
                            >
                                → Go to JSON Restore Page
                            </Button>
                        </VStack>
                    </Box>
                </Box>
            </ChakraProvider>
        </>
    );
};

export default JsonBackup;
